import { Stack } from '@mui/material';
import AssignmentDrawerRow from './AssignmentDrawerRow';
import {
  Checkbox,
  DateInput,
  NumberInput,
  TextInput,
  ClearAfterUnmountWrapper,
  DropdownInputOnlyBoolean,
} from 'framework/form';
import RolesDropdown from 'domain/shared/assignment/createupdate/RolesDropdown';
import AssignmentServiceCatalogTargetInput from 'domain/shared/assignment/target/shared/input/AssignmentServiceCatalogTargetInput';
import AssignmentTypeInput from 'domain/shared/assignment/input/AssignmentTypeInput';
import { NotDismissedEmployeeInput } from 'domain/shared/employee';
import EmployeeGroupDropdown from 'domain/shared/assignment/group/shared/input/EmployeeGroupDropdown';
import AssignmentTargetInput from 'domain/shared/assignment/target/shared/input/AssignmentTargetInput';
import AssignmentBudgetsDropdown from 'domain/shared/assignment/input/AssignmentBudgetsDropdown';
import { SPOF_TOOLTIP } from 'domain/shared/assignment/constants';
import useAssignmentFormState, {
  isIndividualAssignment,
  isGroupAssignment,
} from 'domain/shared/assignment/createupdate/useAssignmentFormState';

const AssignmentDrawerFields = ({ isCreateForm }: { isCreateForm: boolean }) => {
  const {
    hasInitialEmployeeId,
    hasInitialEmployeeGroup,
    isGroupAssignmentsEnabled,
    isSpofVisible,
    isSecondaryLeadVisible,
    isStartedAssignment,
  } = useAssignmentFormState();
  return (
    <Stack gap={2}>
      <AssignmentDrawerRow>
        <AssignmentTypeInput
          source="assignmentType"
          isVisible={isGroupAssignmentsEnabled && !hasInitialEmployeeId && !hasInitialEmployeeGroup && isCreateForm}
        />
      </AssignmentDrawerRow>
      <AssignmentDrawerRow>
        <NotDismissedEmployeeInput source="ownerId" label="Owner" nameSource="ownerName" />
        <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
          <NotDismissedEmployeeInput
            source="employeeId"
            label="Worker"
            nameSource="employeeName"
            includeInterns
            includeContractors
            disabled={hasInitialEmployeeId || !isCreateForm}
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={isGroupAssignment}>
          <EmployeeGroupDropdown
            source="employeeGroupId"
            label="Service"
            placeholder="Select service"
            errorSource="employeeGroup"
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
          <DropdownInputOnlyBoolean placeholder="Bench" source="bench" label="Bench" />
        </ClearAfterUnmountWrapper>
      </AssignmentDrawerRow>
      <AssignmentDrawerRow>
        <AssignmentTargetInput
          mapResults={(target: any) => {
            return {
              id: target.id,
              text: target.displayName,
              target,
            };
          }}
          source="target"
          label="Target"
          nameSource="target.displayName"
          errorSource="rbsTreeNode"
          isVisible={assignment => !assignment?.target?.targetId}
        />
        <AssignmentServiceCatalogTargetInput
          mapResults={(target: any) => {
            return {
              id: target.id,
              text: target.name,
              target,
            };
          }}
          source="target"
          label="Service-catalog target"
          nameSource="name"
          isVisible={assignment => !assignment?.target?.id}
        />
        <TextInput
          source="target.budgetInfo.defaultBudget.name"
          label="Target's default budget"
          disabled
          isVisible={assignment => !!assignment?.target?.budgetInfo?.defaultBudget}
        />
        <AssignmentBudgetsDropdown
          source="budgetId"
          label="Budget"
          nameSource="budget.name"
          searchString="name"
          helpText="Specify budget if it differs from the default target budget"
          isVisible
        />
      </AssignmentDrawerRow>
      <AssignmentDrawerRow>
        <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
          <RolesDropdown source="roleOnProject" label="Role" />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
          <Checkbox source="backup" label="Backup" />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={isSpofVisible}>
          <Checkbox source="isSpof" label="SPOF" tooltip={SPOF_TOOLTIP} />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={isSecondaryLeadVisible}>
          <Checkbox source="isSecondaryLead" label="Secondary lead" />
        </ClearAfterUnmountWrapper>
      </AssignmentDrawerRow>
      <AssignmentDrawerRow>
        <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
          <NotDismissedEmployeeInput
            source="backupForEmployeeId"
            label="Backup for"
            nameSource="backupForEmployeeName"
            errorSource="backupFor"
            isVisible={data => data.backup}
            includeInterns
            includeContractors
          />
        </ClearAfterUnmountWrapper>
      </AssignmentDrawerRow>
      <AssignmentDrawerRow>
        <DateInput source="from" disabled={!isCreateForm && isStartedAssignment} />
        <DateInput source="to" />
      </AssignmentDrawerRow>
      <AssignmentDrawerRow>
        <NumberInput source="minPercent" label="Min percent" integerOnly />
        <NumberInput source="maxPercent" label="Max percent" integerOnly />
      </AssignmentDrawerRow>
    </Stack>
  );
};

export default AssignmentDrawerFields;
